<template>
  <form class="tw:space-y-4" @submit.prevent="onSubmit">
    <fieldset>
      <legend v-if="legend" class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
        {{ legend }}
      </legend>
      <div v-if="filterDefinition.type === FilterDefinitionTypeEnum.BOOLEAN" class="tw:flex tw:items-center tw:gap-2">
        <Toggle :id="'frm-filter-toggle-' + filterDefinition.name" v-model="currentFilterValue" />
        <label :for="'frm-filter-toggle-' + filterDefinition.name" class="tw:text-sm">
          {{ filterDefinition.label }}
        </label>
      </div>
      <div v-else-if="[FilterDefinitionTypeEnum.SELECT, FilterDefinitionTypeEnum.ASYNC_SELECT].includes(filterDefinition.type)">
        <FuseSearch
          :data="filterDefinition.options || []"
          :exactMatchNumber="0.2"
          :keys="['label']"
        >
          <template #search="{ modelValue, updateModelValue }">
            <div class="tw:mb-2">
              <FuseSearchInput
                :modelValue="modelValue"
                @update:modelValue="updateModelValue"
                class="form-control tw:w-full"
              />
            </div>
          </template>
          <template #default="{ results }">
            <div class="tw:max-h-48 tw:overflow-y-auto tw:space-y-2">
              <div v-for="option in results" :key="(option as any).value" class="tw:flex tw:items-center">
                <input type="checkbox" :id="'frm-filter-toggle-' + filterDefinition.name  + '-' + (option as any).value" :value="(option as any).value" v-model="currentFilterValue" class="tw:mr-2" />
                <label :for="'frm-filter-toggle-' + filterDefinition.name  + '-' + (option as any).value" class="tw:text-sm">
                  {{ (option as any).label }}
                </label>
              </div>
            </div>
          </template>
        </FuseSearch>
        <div
          v-if="filterDefinition.toggle"
          class="tw:mt-3 tw:pt-3 tw:border-t tw:border-gray-200 tw:flex tw:items-center tw:gap-2"
        >
          <Toggle :id="'frm-filter-option-' + filterDefinition.name" v-model="toggleValue" />
          <label :for="'frm-filter-option-' + filterDefinition.name" class="tw:text-sm">
            {{ filterDefinition.toggle.label }}
          </label>
        </div>
      </div>
      <div v-else-if="filterDefinition.type === FilterDefinitionTypeEnum.PERIOD" class="tw:space-y-3">
        <div class="tw:space-y-3">
          <div>
            <label :for="'frm-filter-period-from-' + filterDefinition.name" class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
              {{ $t('From date') }}
            </label>
            <input
              :type="periodInputType"
              :id="'frm-filter-period-from-' + filterDefinition.name"
              v-model="periodFrom"
              class="form-control tw:w-full"
            />
          </div>
          <div>
            <label :for="'frm-filter-period-to-' + filterDefinition.name" class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
              {{ $t('To date') }}
            </label>
            <input
              :type="periodInputType"
              :id="'frm-filter-period-to-' + filterDefinition.name"
              v-model="periodTo"
              class="form-control tw:w-full"
            />
          </div>
        </div>
      </div>
      <div v-else-if="filterDefinition.type === FilterDefinitionTypeEnum.TEXT">
        <input
          type="text"
          :id="'frm-filter-text-' + filterDefinition.name"
          v-model="currentFilterValue"
          class="form-control tw:w-full"
          :placeholder="filterDefinition.label"
        />
      </div>
      <div v-else>
        {{ $t('Unsupported filter type: {type}', { type: filterDefinition.label } ) }}
      </div>
    </fieldset>
    <div class="tw:flex tw:justify-start tw:gap-2">
      <button type="submit" class="btn btn-primary tw:w-full"
        :disabled="isButtonDisabled">
        {{ $t('Apply') }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import dayjs from 'dayjs'
import Toggle from '@/components/ui/Toggle.vue'
import { type FilterDefinition, FilterDefinitionTypeEnum } from "./filters";
import { readToggle, stripToggleTokens, withToggleToken } from './composables/useFilterValue';
import FuseSearch from "@/components/atoms/fuse-search/FuseSearch.vue";
import FuseSearchInput from "@/components/atoms/fuse-search/FuseSearchInput.vue";

const props = withDefaults(
  defineProps<{
    filterDefinition: FilterDefinition
    legend?: string | undefined,
    filterValue?: string[] | boolean | string | null | undefined,
  }>(),
  {
    filterValue: undefined,
  }
)

const emit = defineEmits<{
  setValue: [value: string[] | boolean | string]
}>()

/**
 * Normalizes the filter value based on the filter type
 */
function normalizeFilterValue(
  filterDefinition: FilterDefinition,
  value: string[] | boolean | string | null | undefined
): string[] | boolean | string {
  if (value === null || value === undefined) {
    if (filterDefinition.type === FilterDefinitionTypeEnum.BOOLEAN) {
      return false
    } else if ([FilterDefinitionTypeEnum.PERIOD, FilterDefinitionTypeEnum.TEXT].includes(filterDefinition.type)) {
      return ''
    }
    return []
  }

  if (filterDefinition.type === FilterDefinitionTypeEnum.BOOLEAN) {
    return Boolean(value)
  }

  if ([FilterDefinitionTypeEnum.PERIOD, FilterDefinitionTypeEnum.TEXT].includes(filterDefinition.type)) {
    return typeof value === 'string' ? value : ''
  }

  // The toggle token rides along the values; it is not an option to tick.
  return stripToggleTokens(Array.isArray(value) ? value : [String(value)])
}

/**
 * Parses period value from string format "from:to".
 *
 * A plain split(':') is not enough once bounds carry a time: an ISO 8601 bound has colons of its
 * own. The separator is the colon introducing the next bound, which always opens with a
 * four-digit year, or a leading/trailing one when only one side is set.
 */
function parsePeriodValue(value: string): { from: string; to: string } {
  if (!value) {
    return { from: '', to: '' }
  }
  const parts = value.split(/^:|:$|:(?=\d{4}-)/)
  return {
    from: parts[0] || '',
    to: parts[1] || ''
  }
}

/** With a time, bounds travel as ISO 8601 while the input shows the viewer's local time. */
const periodInputType = computed(() => (props.filterDefinition.with_time ? 'datetime-local' : 'date'))

function toInputValue(bound: string): string {
  if (!bound || !props.filterDefinition.with_time) {
    return bound || ''
  }
  return dayjs(bound).format('YYYY-MM-DDTHH:mm')
}

function toEmittedValue(bound: string): string {
  if (!bound || !props.filterDefinition.with_time) {
    return bound || ''
  }
  return dayjs(bound).toISOString()
}

const currentFilterValue = ref(normalizeFilterValue(props.filterDefinition, props.filterValue))
const toggleValue = ref(false)
const periodFrom = ref('')
const periodTo = ref('')

/** The toggle state the incoming value states, or its default when it states none. */
function readToggleValue(
  filterDefinition: FilterDefinition,
  value: string[] | boolean | string | null | undefined
): boolean {
  if (!filterDefinition.toggle) {
    return false
  }

  return readToggle(Array.isArray(value) ? value : [], filterDefinition.toggle)
}

// Initialize period values when filter definition or value changes
watch(
  () => [props.filterDefinition, props.filterValue],
  ([newFilterDefinition, newFilterValue]) => {
    if (newFilterDefinition && typeof newFilterDefinition === 'object' && 'type' in newFilterDefinition) {
      currentFilterValue.value = normalizeFilterValue(
        newFilterDefinition,
        newFilterValue as string[] | boolean | string | null | undefined
      )
      toggleValue.value = readToggleValue(
        newFilterDefinition,
        newFilterValue as string[] | boolean | string | null | undefined
      )

      if (newFilterDefinition.type === FilterDefinitionTypeEnum.PERIOD) {
        const periodValue = parsePeriodValue(currentFilterValue.value as string)
        periodFrom.value = toInputValue(periodValue.from)
        periodTo.value = toInputValue(periodValue.to)
      }
    }
  },
  { immediate: true }
)

// Synchronize period inputs with combined value
watch([periodFrom, periodTo], () => {
  if (props.filterDefinition.type === FilterDefinitionTypeEnum.PERIOD) {
    currentFilterValue.value = `${toEmittedValue(periodFrom.value)}:${toEmittedValue(periodTo.value)}`
  }
})

const hasPeriodValue = computed(() => {
  if (props.filterDefinition.type !== FilterDefinitionTypeEnum.PERIOD) {
    return true
  }
  return periodFrom.value !== '' || periodTo.value !== ''
})

const isButtonDisabled = computed(() => {
  if (props.filterDefinition.type === FilterDefinitionTypeEnum.BOOLEAN) {
    return false
  }

  if (props.filterDefinition.type === FilterDefinitionTypeEnum.PERIOD) {
    return !hasPeriodValue.value
  }

  return !currentFilterValue.value ||
    (Array.isArray(currentFilterValue.value) && currentFilterValue.value.length === 0)
})

function onSubmit(): void {
  let valueToEmit = currentFilterValue.value
  
  // For PERIOD type, only emit if there's at least one date
  if (props.filterDefinition.type === FilterDefinitionTypeEnum.PERIOD) {
    if (!hasPeriodValue.value) {
      return
    }
    valueToEmit = `${periodFrom.value}:${periodTo.value}`
  }

  if (props.filterDefinition.toggle && Array.isArray(valueToEmit)) {
    valueToEmit = withToggleToken(valueToEmit, props.filterDefinition.toggle, toggleValue.value)
  }

  emit('setValue', valueToEmit)
}

</script>

